import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { AxiosResponse } from 'axios';
import { BusinessProfile } from '@views/Profile/Profile';
import { StarterChecklistDto } from '@components/StarterChecklist/StarterChecklist';
import { StarterChecklist } from '@components/StarterChecklist';
import { Notification } from '@components/Notification';
import { NotificationErrorBoundary } from '@views/ErrorBoundary';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Portal,
} from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { FeatureFlagPanel } from '@components/FeatureFlagPanel';
import {
  businessProfileAtom,
  errorResponseStatusCodeAtom,
  responseErrorMessagesAtom,
  confirmationMessageAtom,
  isPublicPageAtom,
  notificationsAtom,
  showNotificationAtom,
  versionAtom,
  xHeaderVersionAtom,
} from '../../store/jotai';
import { useAuthHttp } from '../../hooks';
import {
  STARTER_CHECKLIST_GET,
  VERSION,
  BUSINESS_PROFILE_GET,
} from '../../constants';
import { Button } from '../../tailwindComponents/Button';

interface AppShellProps extends ComponentPropsWithoutRef<'div'> {
  sx?: any;
}

export function AppShell({ children, ...props }: AppShellProps) {
  const authHttp = useAuthHttp();
  const [searchParams] = useSearchParams();
  const [notifications, setNotifications] = useAtom(notificationsAtom);
  const [version, setVersionAtom] = useAtom(versionAtom);
  const [errorResponseStatusCode, setErrorResponseStatusCode] = useAtom(
    errorResponseStatusCodeAtom
  );
  const [responseErrorMessages, setResponseErrorMessages] = useAtom(
    responseErrorMessagesAtom
  );
  const [confirmationMessage, setConfirmationMessage] = useAtom(
    confirmationMessageAtom
  );
  const [starterChecklist, setStarterChecklist] =
    useState<StarterChecklistDto>();

  const [showNotification, setShowNotification] = useAtom(showNotificationAtom);
  const isPublicPage = useAtomValue(isPublicPageAtom);
  const setBusinessProfile = useSetAtom(businessProfileAtom);
  const xHeaderVersion = useAtomValue(xHeaderVersionAtom);

  const fetchBusinessProfile = async () => {
    try {
      const { data }: AxiosResponse<BusinessProfile> = await authHttp.get(
        BUSINESS_PROFILE_GET
      );
      if (data) {
        setBusinessProfile(data.businessProfile);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  useEffect(() => {
    if (isPublicPage) return;
    async function fetchVersion() {
      try {
        const res = await fetch(VERSION);
        const data = await res.text();
        if (data) setVersionAtom(data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('error: ', error);
      }
    }
    fetchVersion();
    fetchBusinessProfile();
  }, []);

  useEffect(() => {
    if (errorResponseStatusCode === 400) {
      const bodyElement = document.body;
      bodyElement.style.pointerEvents = '';
    }
  }, [errorResponseStatusCode]);

  const clearErrorState = () => {
    setErrorResponseStatusCode(null);
    setResponseErrorMessages([]);
  };

  const fetchStarterChecklist = useCallback(async () => {
    try {
      const { data } = await authHttp.get(STARTER_CHECKLIST_GET);
      if (data.starterChecklist) {
        setStarterChecklist(data.starterChecklist);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching starter checklist:', error);
    }
  }, [authHttp]);

  useEffect(() => {
    if (isPublicPage) return;
    if (!starterChecklist) {
      fetchStarterChecklist();
    }
  }, [starterChecklist]);

  useEffect(() => {
    const notification = notifications?.[0];
    if (
      notification &&
      notification.notificationType === 'StarterChecklist.Updated'
    ) {
      fetchStarterChecklist();
    }
  }, [notifications]);

  useEffect(() => {
    if (notifications?.length > 0) {
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    }
  }, [showNotification]);

  return (
    <div {...props}>
      {children}
      {searchParams.get('featureFlags') && <FeatureFlagPanel />}
      {import.meta.env.MODE !== 'development' && (
        <div className='fixed bottom-2 right-2 z-10 text-xs text-gray-400'>
          {version !== xHeaderVersion ? (
            <Button
              label='Click to update to new version!'
              intent='primary'
              size='sm'
              onClick={() => window.location.reload()}
              type='button'
            />
          ) : (
            <span>Version - v{version}</span>
          )}
        </div>
      )}
      {confirmationMessage && (
        <div className='pointer-events-none fixed inset-x-0 bottom-0 z-50 px-6 pb-6'>
          <div className='pointer-events-auto mx-auto max-w-xl rounded-xl bg-white p-6 shadow-lg ring-1 ring-gray-900/10'>
            <div className='flex items-start gap-4'>
              <CheckCircleIcon
                className='h-6 w-6 flex-shrink-0 text-green-400'
                aria-hidden='true'
              />
              <div className='flex-1'>
                <p className='text-sm font-medium text-gray-900'>
                  {confirmationMessage}
                </p>
              </div>
              <button
                type='button'
                className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                onClick={() => {
                  setConfirmationMessage('');
                }}
              >
                <span className='sr-only'>Dismiss</span>
                <XMarkIcon className='h-5 w-5' aria-hidden='true' />
              </button>
            </div>
          </div>
        </div>
      )}
      {errorResponseStatusCode && errorResponseStatusCode !== 400 && (
        <div className='pointer-events-none fixed inset-x-0 bottom-0 z-50 px-6 pb-6'>
          <div className='pointer-events-auto mx-auto max-w-xl rounded-xl bg-white p-6 shadow-lg ring-1 ring-gray-900/10'>
            <div className='flex items-start gap-4'>
              <XCircleIcon
                className='h-6 w-6 flex-shrink-0 text-red-400'
                aria-hidden='true'
              />
              <div className='flex-1'>
                {responseErrorMessages &&
                  responseErrorMessages.length === 1 && (
                    <p className='text-sm font-medium text-gray-900'>
                      {responseErrorMessages[0]}
                    </p>
                  )}
                {responseErrorMessages && responseErrorMessages.length > 1 && (
                  <div className='text-sm text-gray-900'>
                    <p className='font-medium'>Something has gone wrong:</p>
                    {responseErrorMessages.map((message) => (
                      <p key={message} className='mt-1'>
                        - {message}
                      </p>
                    ))}
                  </div>
                )}
                {(!responseErrorMessages ||
                  responseErrorMessages.length < 1) && (
                  <p className='text-sm font-medium text-gray-900'>
                    Something has gone wrong. If the error persists, please
                    contact us.
                  </p>
                )}
              </div>
              <button
                type='button'
                className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                onClick={() => {
                  clearErrorState();
                }}
              >
                <span className='sr-only'>Dismiss</span>
                <XMarkIcon className='h-5 w-5' aria-hidden='true' />
              </button>
            </div>
          </div>
        </div>
      )}
      {errorResponseStatusCode && errorResponseStatusCode === 400 && (
        <Portal>
          <Dialog
            open={errorResponseStatusCode === 400}
            onClose={() => {
              setErrorResponseStatusCode(null);
              setResponseErrorMessages([]);
            }}
            className='relative z-modalPanel'
          >
            <DialogBackdrop
              transition
              className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
            />
            <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
              <div className='flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0'>
                <DialogPanel
                  transition
                  className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95'
                >
                  <div className='absolute right-0 top-0 hidden pr-4 pt-4 sm:block'>
                    <button
                      type='button'
                      className='rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2'
                      onClick={() => {
                        setErrorResponseStatusCode(null);
                        setResponseErrorMessages([]);
                      }}
                    >
                      <span className='sr-only'>Close</span>
                      <XMarkIcon className='h-6 w-6' aria-hidden='true' />
                    </button>
                  </div>
                  <div className='sm:flex sm:items-start'>
                    <div className='mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10'>
                      <ExclamationTriangleIcon
                        className='h-6 w-6 text-red-600'
                        aria-hidden='true'
                      />
                    </div>
                    <div className='mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left'>
                      <DialogTitle
                        as='h3'
                        className='text-base font-semibold leading-6 text-gray-900'
                      >
                        Something went wrong
                      </DialogTitle>
                      <div className='mt-2'>
                        {responseErrorMessages &&
                          responseErrorMessages.length === 1 && (
                            <p className='text-sm text-gray-500'>
                              {responseErrorMessages[0]}
                            </p>
                          )}
                        {responseErrorMessages &&
                          responseErrorMessages.length > 1 && (
                            <div className='text-sm text-gray-500'>
                              {responseErrorMessages.map((message) => (
                                <p key={message} className='mt-1'>
                                  - {message}
                                </p>
                              ))}
                            </div>
                          )}
                        {(!responseErrorMessages ||
                          responseErrorMessages.length < 1) && (
                          <p className='text-sm text-gray-500'>
                            Something has gone wrong. If the error persists,
                            please contact us.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </div>
            </div>
          </Dialog>
        </Portal>
      )}
      {starterChecklist && (
        <StarterChecklist starterChecklist={starterChecklist} />
      )}
      <NotificationErrorBoundary resetKey={notifications?.[0]?.id}>
        <Notification
          notification={notifications?.[0]}
          show={showNotification}
          setShow={() => setShowNotification(false)}
        />
      </NotificationErrorBoundary>
    </div>
  );
}
